/* eslint-disable */
import React from "react";
import { Skeleton } from "@material-ui/lab";
import { CalendarMonth } from "../../../controls/CalendarMonth";
import { CalendarHierarchy, InputCalendarHierarchyItem } from "../../../controls/CalendarHierarchy";
import { ICalendarItem, useDictionary, useParameter } from "../../../shared";
import { dictionaryService } from "../../../dataFlow";
import { equal, uniq } from "../../../../../shared";
import { findClosestPrecedingDate, getDateParts, getDateWithoutFrequency, getDateId } from "../utils/dateHelpers";
import { HierarchyControl, HierarchyControlItem } from "../../../../basf-ui";

interface ControlItem extends HierarchyControlItem<number> {
  frequency: number;
}

const [yearFrequency, monthFrequency, weekFrequency] = [1, 4, 7];

export const StartDateControl: React.FC = () => {
  const [dictItems, , loading] = useDictionary(dictionaryService, "calendar-origin");
  const [commodityValue] = useParameter<number | null>("@commodities");
  const [frequencyValue] = useParameter<number | null>("@frequencies");
  const [dictItemsLastWeek] = useDictionary(dictionaryService, "slides-f-last-calendar", undefined, {
    attributes: "",
    unit: "",
    commodities: String(commodityValue),
    countries: "",
    slide: 33,
  });
  const lastWeekDate = dictItemsLastWeek && dictItemsLastWeek[0] ? Number(dictItemsLastWeek[0].id) : getDateId(new Date(), weekFrequency);
  const [controlItems, setControlItems] = React.useState<ControlItem[]>([]);
  const [calendarValue, setCalendarValue] = useParameter<number>("@start_date");
  const [endDateCalendarValue, setEndDateCalendarValue] = useParameter<number>("@end_date");

  React.useEffect(() => {
    if (!calendarValue || (calendarValue && getDateWithoutFrequency(calendarValue) > getDateWithoutFrequency(lastWeekDate))) {
      const lastWeekParts = getDateParts(lastWeekDate);
      const twoYearsBack = Number(`${lastWeekParts.frequency}${Number(lastWeekParts.year) - 2}${lastWeekParts.month}${lastWeekParts.day}`);
      const newDate = findClosestPrecedingDate(
        twoYearsBack,
        (controlItems || ([] as any)).map((item) => item.id)
      );
      const newDateFreq = findClosestPrecedingDate(
        twoYearsBack,
        (controlItems || ([] as any)).map((item) => item.id),
        frequencyValue
      );
      if (newDate) {
        setCalendarValue(newDate);
      } else if (newDateFreq) {
        setCalendarValue(newDateFreq);
      }
    } else {
      const newDate = findClosestPrecedingDate(
        calendarValue,
        (controlItems || ([] as any)).map((item) => item.id)
      );
      const newDateFreq = findClosestPrecedingDate(
        calendarValue,
        (controlItems || ([] as any)).map((item) => item.id),
        frequencyValue
      );
      if (newDate && newDate !== calendarValue) {
        setCalendarValue(newDate);
      } else if (!newDate && newDateFreq) {
        setCalendarValue(newDateFreq);
      }
    }
  }, [JSON.stringify(controlItems), lastWeekDate, frequencyValue]);

  React.useEffect(() => {
    const newDate = findClosestPrecedingDate(
      calendarValue,
      (controlItems || ([] as any[])).map((item) => item.id),
      frequencyValue
    );
    if (newDate) {
      setCalendarValue(newDate);
    }
  }, [frequencyValue]);

  React.useEffect(() => {
    const candiatesControlItems = dictItems?.map<ControlItem>((item) => ({
      id: Number(item.id),
      parent: item.getValue<number>("parent"),
      name: item.getValue<string>("name") || "",
      frequency: item.getValue<number>("frequencyId")!,
    }));

    const [minDate, maxDate] = [20020101, getDateWithoutFrequency(lastWeekDate)];
    const datesInRengeLimit = (candiatesControlItems || []).filter((item) => {
      return minDate <= getDateWithoutFrequency(item.id as number) && getDateWithoutFrequency(item.id as number) <= maxDate;
    });
    const nextControlItems = datesInRengeLimit.reduce((result, item) => {
      if (item.frequency === yearFrequency) {
        result.push(item);
      } else if (item.frequency === monthFrequency) {
        const parentQuartal = dictItems?.find((dictItem) => equal(+dictItem.id, item.parent));
        const parentYearId = parentQuartal?.getValue<number>("parent")!;
        if (parentQuartal && parentYearId) {
          item.parent = parentYearId;
          result.push(item);
        }
      } else if (item.frequency === weekFrequency && frequencyValue === weekFrequency) {
        const paretMonth = dictItems?.find((dictItem) => equal(Number(dictItem.id), item.parent));
        if (paretMonth) {
          result.push(item);
        }
      }
      return result;
    }, [] as any);

    setControlItems(nextControlItems);
  }, [JSON.stringify(dictItems), lastWeekDate, frequencyValue, endDateCalendarValue]);

  const handlerChange = React.useCallback(
    (value: unknown) => {
      const weekOrMonthOrYearId = value as number;
      if (weekOrMonthOrYearId) {
        setCalendarValue(weekOrMonthOrYearId);
        if (!endDateCalendarValue || getDateWithoutFrequency(weekOrMonthOrYearId) > getDateWithoutFrequency(endDateCalendarValue)) {
          setEndDateCalendarValue(weekOrMonthOrYearId);
        }
      }
    },
    [dictItems, endDateCalendarValue, setCalendarValue]
  );

  return <HierarchyControl loading={loading} label="Start date" value={calendarValue} onChange={handlerChange} items={controlItems} />;
};
